import React, { FC, useEffect, useMemo, useState } from 'react';
import { BulkEditProps } from './BulkEditProps';
import DefaultPageLayout from '../../common/DefaultPageLayout';
import produce from 'immer';
import { Project, TableProject } from '../../models/project';
import BulkEditFinancialsTable from '../../components/BulkEditFinancialsTable';
import CustomTabsComponent from '../../components/CustomTabsComponent';
import { Stack } from '@mui/material';
import BulkEditTable from '../../components/BulkEditTable';
import {
  checkForFinancialsError,
  mapProjectsForTable,
  reverseFinancialData,
} from '../../components/BulkEditFinancialsTable/utils';
import { useTypedSelector } from '../../store';
import {
  bulkProjects,
  getLoading,
  getProjectsForBulk,
  selectSolutions,
} from '../../store/projects/selectors';
import {
  fetchProjectsForBulk,
  setProjectToEdit,
  setSolutions,
  UpdateProjectsBulk,
} from '../../store/projects/actionWrapper';
import { projectsActions } from '../../store/projects/slice';
import { useDispatch } from 'react-redux';
import Button from '@ingka/button';
import { useHistory } from 'react-router-dom';
import ArrowLeft from '@ingka/ssr-icon/paths/arrow-left';
import Text from '@ingka/text';
import { extendFiscalYears } from '../../components/ProjectTab/ProjectDatesFields/utils';
import { getFY, getFyNumber } from '../../utils/calculation';
import { selectUserRole } from '../../store/userData/selectors';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { requiredFields } from '../../components/BulkEditTable/utils';
import { enqueueErrorSnackbar } from '../../store/notifications/actionWrapper';
import { selectCurrencyArray } from '../../store/currency/selectors';
import { selectResponsibleOptionsList } from '../../store/users/selectors';
import { useAnalytics } from '../../hooks/useAnalytics';
import BulkSolutionModal from '../../components/BulkSolutionModal';
import { ESTABLISHMENT_TYPE } from '../../components/ProjectTypeSelect/utils';
import { stripOfNewId } from '../BulkCreate/utils';
import { shouldAutoPromoteToDraft } from '../../utils/fieldEditingRules';

const BulkEdit: FC<BulkEditProps> = () => {
  const { actionStart, actionEnd, actionDiscarded } =
    useAnalytics('projectUpdated');
  const generalInfoRef = useGridApiRef();
  const dispatch = useDispatch();
  const currencies = useTypedSelector(selectCurrencyArray);
  const history = useHistory();
  const projectIds = useTypedSelector(bulkProjects);
  const stateProjects = useTypedSelector(getProjectsForBulk);
  const storeSolutions = useTypedSelector(selectSolutions);
  const userList = useTypedSelector(selectResponsibleOptionsList);
  const loading = useTypedSelector(getLoading);
  const [projects, setProjects] = useState([]);
  const [solutionModal, setSolutionModal] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('information');
  const [financials, setFinancials] = useState([]);
  const role = useTypedSelector(selectUserRole);
  const onRowChangeHandler = (id: string, field: string, value: any) => {
    const newProjects = produce(projects, (draft) => {
      const project = draft.find((p) => p.id === id);
      if (project) {
        project[field] = value;
        if (
          field === 'partOfCrp' &&
          shouldAutoPromoteToDraft(
            project.projectStatus,
            value,
            project.projectSource,
          )
        ) {
          project.projectStatus = 'DRAFT';
        }
        if (
          field === 'targetProjectStartDate' &&
          new Date(value) > new Date(project.targetProjectEndDate)
        ) {
          project.targetProjectEndDate = value;
        }
        if (
          field === 'targetProjectEndDate' &&
          new Date(value) > new Date(project.targetProjectStartDate)
        ) {
          project.targetProjectEndDate = value;
        }

        // EXTEND FISCAL YEARS
        if (
          field === 'targetProjectStartDate' ||
          field === 'targetProjectEndDate'
        ) {
          project.investments = extendFiscalYears(
            project.investments,
            getFyNumber(getFY(0, project.targetProjectStartDate)),
            getFyNumber(getFY(0, project.targetProjectEndDate)),
          );
        }
      }
    });
    setProjects(newProjects);
  };
  const closeSolutionModal = () => {
    setSolutionModal(false);
    setActiveProjectId(null);
    dispatch(projectsActions.clearProject());
    dispatch(setSolutions([]));
  };
  const goBackHandler = () => {
    history.push('/');
    actionDiscarded();
  };

  const OnTabChangeHangler = (value) => {
    if (value === 'information') {
      setProjects(reverseFinancialData(projects, financials));
    } else {
      // we need to add missing fy, if dates were changed like we do in the modals
      setFinancials(mapProjectsForTable(projects));
    }

    setActiveTab(value);
  };
  useEffect(() => {
    dispatch(fetchProjectsForBulk(projectIds));
    actionStart();
  }, [projectIds]);
  useEffect(() => {
    setProjects(stateProjects);
  }, [stateProjects]);

  const activeProject: TableProject = useMemo(() => {
    return activeProjectId
      ? projects.find((p) => p.id === activeProjectId)
      : null;
  }, [activeProjectId]);

  const onFinancialsRowChangeHandler = (
    id: string,
    field: string,
    value: string,
  ) => {
    const newFinancials = financials.map((project) => {
      if (project.id === id) {
        const [fy, fieldName] = field.split('-');
        if (fieldName === 'landCost') {
          return {
            ...project,
            [field]: value,
            [`${fy}-error`]: +value > +project?.[`${fy}-propertyInvestment`],
          };
        }
        if (fieldName === 'propertyInvestment') {
          return {
            ...project,
            [field]: value,
            [`${fy}-error`]: +value < +project?.[`${fy}-landCost`],
          };
        }
        return { ...project, [field]: value };
      }
      return project;
    });
    setFinancials(newFinancials);
  };
  const updateProjects = () => {
    let projectsToSend = projects;
    if (activeTab === 'financials') {
      projectsToSend = reverseFinancialData(projects, financials);
    }
    const valid =
      projectsToSend.filter((project) => {
        let valid = true;
        requiredFields.forEach((field) => {
          if (!project[field]) {
            valid = false;
          }
        });
        if (
          [
            ESTABLISHMENT_TYPE.IMPROVE,
            ESTABLISHMENT_TYPE.FORMAT_CHANGE,
          ].includes(project.establishmentType) &&
          !project.solutions.length
        ) {
          valid = false;
        }

        if (checkForFinancialsError(financials)) {
          valid = false;
        }
        return valid;
      })?.length === projectsToSend.length;
    if (!valid) {
      dispatch(
        enqueueErrorSnackbar(
          'Validation Failed, Please fill needed fields and check if financials are correct.',
        ),
      );
      generalInfoRef.current.setState((prev) => {
        return {
          ...prev,
          customParams: { validationTriggered: true },
        };
      });
    } else {
      actionEnd({
        eventSubType: 'BulkUpdate',
        context: { projectsCount: projectsToSend.length },
      });
      const mappedProjects = projectsToSend.map((p) => {
        const copy = { ...p };
        const solutions = p.solutions.map(stripOfNewId);
        return { ...copy, solutions };
      });
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      dispatch(UpdateProjectsBulk({ payload: mappedProjects, history }));
    }
  };
  const addSolutionsHandler = (id) => {
    const project = projects.find((p) => p.id === id);
    dispatch(setSolutions(project?.solutions || []));
    dispatch(setProjectToEdit(project || {}));
    setSolutionModal(true);
    setActiveProjectId(id);
  };

  const saveSolutionsHanlder = () => {
    const projectsCopy = [...projects];
    const projectIndex = projectsCopy.findIndex(
      (p) => p.id === activeProjectId,
    );
    if (projectIndex !== -1) {
      projectsCopy[projectIndex] = {
        ...projectsCopy[projectIndex],
        solutions: storeSolutions,
      };
      setProjects(projectsCopy);
    }
    dispatch(projectsActions.clearProject());
    closeSolutionModal();
  };
  return (
    <>
      <DefaultPageLayout
        sidebar={() => <></>}
        mainPart={() => (
          <Stack spacing={2} flexGrow={1} overflow={'auto'} p={0}>
            <Stack spacing={2} width={'fit-content'}>
              <Stack flexDirection={'row'} gap={2}>
                <Button
                  type="secondary"
                  data-testid={'bulk-edit'}
                  ssrIcon={ArrowLeft}
                  onClick={goBackHandler}
                >
                  {'Back to projects'}
                </Button>
                <Button
                  type="emphasised"
                  data-testid={'update-projects'}
                  onClick={updateProjects}
                >
                  {'Update Projects'}
                </Button>
              </Stack>
              <Text textStyle="Heading.M" tagName="h3">
                {'Bulk Update'}
              </Text>
            </Stack>

            <Stack flexGrow={1}>
              <CustomTabsComponent
                activeTab={activeTab}
                onTabChange={(e, value) => OnTabChangeHangler(value)}
                tabList={[
                  { value: 'information', label: 'Project Information' },
                  { value: 'financials', label: 'Financial Information' },
                ]}
                tabErrors={null}
              />
              <div
                role="tabpanel"
                hidden={'financials' !== activeTab}
                id={`tabPanel-${activeTab}`}
                data-testid={'financials-tab'}
                aria-labelledby={`simple-tab-${activeTab}`}
                style={{ height: '100%', overflowX: 'auto' }}
              >
                <Stack spacing={2}>
                  <BulkEditFinancialsTable
                    projects={financials}
                    onChange={onFinancialsRowChangeHandler}
                    currencyRates={currencies}
                  />
                </Stack>
              </div>
              <div
                role="tabpanel"
                hidden={'information' !== activeTab}
                id={`tabPanel-${activeTab}`}
                data-testid={'information-tab'}
                aria-labelledby={`simple-tab-${activeTab}`}
                style={{ height: '100%', overflowX: 'auto' }}
              >
                <Stack spacing={2}>
                  <BulkEditTable
                    loading={loading}
                    userRole={role}
                    projects={projects as unknown as Project[]}
                    onChange={onRowChangeHandler}
                    apiRef={generalInfoRef}
                    userList={userList}
                    onAddSolutionsClicked={addSolutionsHandler}
                  />
                </Stack>
              </div>
            </Stack>
          </Stack>
        )}
      />
      <BulkSolutionModal
        closeHandler={closeSolutionModal}
        saveSolutionsHandler={saveSolutionsHanlder}
        visible={solutionModal}
        project={activeProject}
      />
    </>
  );
};

export default BulkEdit;
